import {hasPermission} from '../../../../../Shared/Auth/AuthenticationUtil';
import {Authority} from '../../../../../Config/Reducers/AuthenticationReducer';
import {AUTHORITIES} from '../../../../../Config/Constants';
import {SystemRoutes} from '../../../../../Utils/RouteUtils';
import {
    ManagementOverviewRequestParams,
} from '../types/ManagementOverviewReport.types';

const END_DATE_TIME_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

export type ManagementOverviewRouteValidationResult =
    | { valid: true; params: ManagementOverviewRequestParams }
    | { valid: false };

export function isAuthorizedForManagementOverviewReport(
    authoritySet: Array<Authority> | null | undefined
): boolean {
    if (hasPermission(authoritySet ?? undefined, [
        AUTHORITIES.SYSADMIN,
        AUTHORITIES.EXPORT_REPORT_MANAGEMENT_REPORT,
    ])) {
        return true;
    }

    return hasPermission(authoritySet ?? undefined, [AUTHORITIES.SEARCH_REPORT])
        && hasPermission(authoritySet ?? undefined, [
            AUTHORITIES.VIEW_PDF_EXPORT,
            AUTHORITIES.PDF_EXPORT_REPORT_MANAGEMENT_REPORT,
        ]);
}

export function dedupePowerStationIds(ids: number[]): number[] {
    const seen = new Set<number>();
    const result: number[] = [];
    ids.forEach((id) => {
        if (!Number.isInteger(id) || id < 1 || seen.has(id)) {
            return;
        }
        seen.add(id);
        result.push(id);
    });
    return result;
}

export function parsePowerStationIdsParam(raw: string | null): number[] {
    if (!raw || raw.trim().length === 0) {
        return [];
    }
    const parts = raw.split(',').map((part) => part.trim()).filter(Boolean);
    const parsed = parts.map((part) => Number(part));
    return dedupePowerStationIds(parsed.filter((id) => Number.isFinite(id)));
}

export function validateManagementOverviewParams(
    params: Partial<ManagementOverviewRequestParams>
): ManagementOverviewRouteValidationResult {
    const powerStationIds = dedupePowerStationIds(params.powerStationIds ?? []);
    const endDateTime = params.endDateTime?.trim() ?? '';

    if (powerStationIds.length === 0 || !END_DATE_TIME_PATTERN.test(endDateTime)) {
        return { valid: false };
    }

    return {
        valid: true,
        params: {
            powerStationIds,
            endDateTime,
        },
    };
}

export function parseManagementOverviewSearchParams(
    searchParams: URLSearchParams
): ManagementOverviewRouteValidationResult {
    return validateManagementOverviewParams({
        powerStationIds: parsePowerStationIdsParam(searchParams.get('powerStationIds')),
        endDateTime: searchParams.get('endDateTime') ?? undefined,
    });
}

export function buildManagementOverviewPrintUrl(
    params: ManagementOverviewRequestParams
): string {
    const validation = validateManagementOverviewParams(params);
    if (!validation.valid) {
        throw new Error('Invalid management overview params');
    }

    const search = new URLSearchParams();
    search.set('powerStationIds', validation.params.powerStationIds.join(','));
    search.set('endDateTime', validation.params.endDateTime);

    return `${SystemRoutes.REPORTS_MANAGEMENT_REPORT_OVERVIEW_PRINT}?${search.toString()}`;
}

export function openManagementOverviewPrintTab(
    params: ManagementOverviewRequestParams
): boolean {
    let url: string;
    try {
        url = buildManagementOverviewPrintUrl(params);
    } catch {
        return false;
    }

    const newTab = window.open('about:blank', '_blank');
    if (!newTab) {
        return false;
    }

    newTab.opener = null;
    newTab.location.href = url;
    return true;
}

export function buildManagementOverviewQueryKey(
    params: ManagementOverviewRequestParams
): readonly [string, string, string] {
    return [
        'management-overview-report',
        params.powerStationIds.join(','),
        params.endDateTime,
    ] as const;
}
